/* eslint-disable react-hooks/exhaustive-deps */
import CircularProgress from '@material-ui/core/CircularProgress';
import { appLanguage } from '@orientaction/reducers';
import { coverBook, imgBook1 } from '@orientaction/utils';
import dateFormat from 'dateformat';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ReactComponent as BookOpenText } from '../../../../Asset/svg/book_open_text.svg';
import { ReactComponent as SportsEsports } from '../../../../Asset/svg/game_controller.svg';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { PageHeader, RedesignLayout, useSharedStyles } from '../../shared';
import RedesignImage from '../../shared/components/RedesignImage';
import ResourceCard, {
  getEbookProgress,
  getSeriousGameCardState,
} from '../components/ResourceCard';
import ResourceSidebar from '../components/ResourceSidebar';
import ResourceTabs from '../components/ResourceTabs';
import { useAttachedResources } from '../hooks/useMyResourcesData';
import locale from '../locale.json';
import { getEbookEnRoute } from '../utils/getEbookEnRoute';
import {
  flattenGroupEntries,
  flattenGroups,
  getGroupedResources,
  getMobileCardId,
  getSectionId,
  normalizeRedirectTab,
  ResourceGroup,
  ResourceTab,
  SummaryItem,
  TAB_ALL,
  TAB_EBOOK,
  TAB_GAME,
} from '../utils/resourceGroups';
import { useAttachedResourcesStyles } from './index.styles';

const formatResourceDate = (date?: string | null) => {
  if (!date) {
    return null;
  }

  const time = new Date(date).getTime();
  return Number.isNaN(time) ? null : dateFormat(date, 'dd/mm/yyyy - HH:MM');
};

const AttachedResources = () => {
  const classes = useAttachedResourcesStyles();
  const sharedClasses = useSharedStyles();
  const history = useHistory();
  const lang: string = useSelector(appLanguage);
  const { allBook, list, assignments, resourcesMap, loading, userInfos } = useAttachedResources();

  const [activeTab, setActiveTab] = useState<ResourceTab>(TAB_ALL);
  const [activeSectionId, setActiveSectionId] = useState('');

  const groupedBookResources = useMemo(
    () =>
      getGroupedResources(
        allBook,
        { ...userInfos, service_resources_map: resourcesMap || userInfos?.service_resources_map },
        assignments
      ),
    [allBook, userInfos, assignments, resourcesMap]
  );
  const groupedGameResources = useMemo(
    () =>
      getGroupedResources(
        list,
        { ...userInfos, service_resources_map: resourcesMap || userInfos?.service_resources_map },
        assignments
      ),
    [list, userInfos, assignments, resourcesMap]
  );

  const flatBookEntries = useMemo(
    () => flattenGroupEntries(groupedBookResources),
    [groupedBookResources]
  );
  const flatGameEntries = useMemo(
    () => flattenGroupEntries(groupedGameResources),
    [groupedGameResources]
  );
  const flatBookResources = useMemo(
    () => flattenGroups(groupedBookResources),
    [groupedBookResources]
  );
  const flatGameResources = useMemo(
    () => flattenGroups(groupedGameResources),
    [groupedGameResources]
  );

  const showBooks = activeTab === TAB_ALL || activeTab === TAB_EBOOK;
  const showGames = activeTab === TAB_ALL || activeTab === TAB_GAME;
  const visibleBookGroups = showBooks ? groupedBookResources : [];
  const visibleGameGroups = showGames ? groupedGameResources : [];
  const hasVisibleResources = visibleBookGroups.length > 0 || visibleGameGroups.length > 0;

  const summarySections = useMemo(
    () =>
      [
        showBooks && visibleBookGroups.length > 0
          ? {
              id: TAB_EBOOK,
              title: locale.ebooksTitle,
              icon: <BookOpenText className={classes.sidebarIcon} />,
              items: visibleBookGroups.map(group => ({
                id: getSectionId(TAB_EBOOK, group.key),
                title: group.title,
                count: group.items.length,
              })),
            }
          : null,
        showGames && visibleGameGroups.length > 0
          ? {
              id: TAB_GAME,
              title: locale.seriousGamesTitle,
              icon: <SportsEsports className={classes.sidebarIcon} />,
              items: visibleGameGroups.map(group => ({
                id: getSectionId(TAB_GAME, group.key),
                title: group.title,
                count: group.items.length,
              })),
            }
          : null,
      ].filter(Boolean) as {
        id: ResourceTab;
        title: string;
        icon: ReactElement;
        items: SummaryItem[];
      }[],
    [showBooks, showGames, visibleBookGroups, visibleGameGroups, classes.sidebarIcon]
  );

  useEffect(() => {
    setActiveSectionId(summarySections[0]?.items[0]?.id || '');
  }, [activeTab, summarySections]);

  useEffect(() => {
    if (!summarySections.length || window.innerWidth < 992) {
      return undefined;
    }

    const sectionIds = summarySections.reduce(
      (acc: string[], section) => acc.concat(section.items.map(item => item.id)),
      []
    );
    const elements = sectionIds
      .map(id => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (!elements.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const visibleEntry = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry?.target?.id) {
          setActiveSectionId(visibleEntry.target.id);
        }
      },
      {
        rootMargin: '-120px 0px -45% 0px',
        threshold: [0.15, 0.3, 0.6],
      }
    );

    elements.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [summarySections]);

  useEffect(() => {
    const data = sessionStorage.getItem('redirect');
    if (!data) {
      return undefined;
    }

    let parseData: any;
    try {
      parseData = JSON.parse(data);
    } catch {
      sessionStorage.removeItem('redirect');
      return undefined;
    }

    if (!parseData) {
      return undefined;
    }

    const redirectTab = normalizeRedirectTab(parseData[0]);
    setActiveTab(redirectTab);

    // Homepage deep-link: only the tab is requested (no specific resource id).
    if (parseData[1] === undefined || parseData[1] === null || parseData[1] === '') {
      sessionStorage.removeItem('redirect');
      return undefined;
    }

    // Legacy mobile flow: scroll to a specific game/ebook card.
    if (window.innerWidth >= 600) {
      sessionStorage.removeItem('redirect');
      return undefined;
    }

    const scrollToMobileCard = () => {
      let cardId = '';

      if (redirectTab === TAB_GAME && flatGameResources) {
        const game = flatGameResources.find(
          (item: any) => item.idGame === parseInt(parseData[1], 10)
        );
        const entry = flatGameEntries.find((item: any) => item.item === game);
        if (entry) {
          cardId = getMobileCardId(TAB_GAME, entry.groupKey, game.idGame);
        }
      }

      if (redirectTab === TAB_EBOOK && flatBookResources) {
        const index = parseInt(parseData[1], 10);
        const book = flatBookResources[index];
        const entry = flatBookEntries.find((item: any) => item.item === book);
        if (entry && book) {
          cardId = getMobileCardId(TAB_EBOOK, entry.groupKey, book?.id || book?.idGame || index);
        }
      }

      if (cardId) {
        document.getElementById(cardId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => sessionStorage.removeItem('redirect'), 1000);
      } else {
        sessionStorage.removeItem('redirect');
      }
    };

    const timeoutId = window.setTimeout(scrollToMobileCard, 300);
    return () => window.clearTimeout(timeoutId);
  }, [list, allBook, flatBookResources, flatGameResources, flatBookEntries, flatGameEntries]);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSectionId(sectionId);
  };

  const redirectEbook = async (book: any, index?: number) => {
    if (book?.isNew) {
      await viewGame(book?.idResult, book?.idEbook, index);
      return;
    }

    if (!book?.isRecrutement) {
      history.push(
        lang !== 'FR'
          ? `/en/${getEbookEnRoute(book?.attributes?.route)}/${book?.id}/${false}/${index}`
          : `${book?.attributes?.route}/${book?.id}/${false}/${index}`
      );
      return;
    }

    history.push(`ebook-recrutement/${book?.id}/${false}/${index}`);
  };

  const viewGame = async (idResult: any, idBook: any, index?: number) => {
    try {
      if (idResult && idResult !== '') {
        history.push(`/ebook/view/${idResult}/${index}`);
        return;
      }

      const response = await apiServiceNative.post('/api/create-ebook-result', {
        serious_ebook_id: idBook,
      });
      history.push(`/ebook/view/${response.id}/${index}`);
    } catch (error) {
      // keep same silent failure as legacy page
    }
  };

  const renderEbookCard = (book: any, index: number, group: ResourceGroup) => {
    const hasConsultation = Boolean(
      book?.isNew ? book?.date_consultation : book?.attributes?.dateConsultation
    );
    const progress = getEbookProgress(hasConsultation);
    const actionLabel = progress.isCompleted ? locale.consult : locale.start;
    const itemId = book?.id || book?.idGame || index;

    return (
      <div key={`${group.key}-${itemId}`} id={getMobileCardId(TAB_EBOOK, group.key, itemId)} className={classes.resourceGridItem}>
        <ResourceCard
          title={book?.isNew ? book?.title : book?.attributes?.name}
          coverSrc={
            book?.isNew
              ? book?.couverture
              : !book?.isRecrutement
              ? `${coverBook}/book${index}.webp`
              : `${imgBook1}/recrutement_cover.png`
          }
          isEbook={true}
          assignationDate={formatResourceDate(book?.attributes?.DateAttribuate) ?? undefined}
          readingDate={
            book?.isNew
              ? formatResourceDate(book?.date_consultation)
              : formatResourceDate(book?.attributes?.dateConsultation)
          }
          progressLabel={progress.label}
          progressValue={progress.value}
          isCompleted={progress.isCompleted}
          isNotStarted={progress.isNotStarted}
          actionLabel={actionLabel}
          linkTo={true}
          onAction={() => redirectEbook(book, index)}
        />
      </div>
    );
  };

  const renderGameCard = (book: any, group: ResourceGroup) => {
    const cardState = getSeriousGameCardState(book);
    const readingDate = book.date_consultation ? formatResourceDate(book.date_consultation) : null;

    return (
      <div
        key={`${group.key}-${book.idGame}`}
        id={getMobileCardId(TAB_GAME, group.key, book.idGame)}
        className={classes.resourceGridItem}>
        <ResourceCard
          title={book.title}
          coverSrc={book.couverture}
          isRestart={book.isRestart}
          readingDate={readingDate}
          progressLabel={cardState.label}
          progressValue={cardState.value}
          isCompleted={cardState.isCompleted}
          isNotStarted={cardState.isNotStarted}
          actionLabel={cardState.actionLabel}
          gameAction={cardState.action}
          gameId={book.idGame}
          idResult={book.idResult}
          idLastPage={book.last_id_page_visited}
        />
      </div>
    );
  };

  const renderBookSections = () =>
    visibleBookGroups.map(group => (
      <section
        key={group.key}
        id={getSectionId(TAB_EBOOK, group.key)}
        className={classes.resourceSection}>
        <div className={classes.sectionHeader}>
          <RedesignImage src="/icons/Talent.svg" alt="" className={classes.sectionIcon} />
          <h3 className={classes.sectionTitle}>{group.title}</h3>
        </div>
        <div className={classes.resourceGrid}>
          {group.items.map((book: any) => {
            const index = flatBookEntries.findIndex(
              (entry: any) => entry.item === book && entry.groupKey === group.key
            );
            return renderEbookCard(book, index, group);
          })}
        </div>
      </section>
    ));

  const renderGameSections = () =>
    visibleGameGroups.map(group => (
      <section
        key={group.key}
        id={getSectionId(TAB_GAME, group.key)}
        className={classes.resourceSection}>
        <div className={classes.sectionHeader}>
          <RedesignImage src="/icons/Talent.svg" alt="" className={classes.sectionIcon} />
          <h3 className={classes.sectionTitle}>{group.title}</h3>
        </div>
        <div className={classes.resourceGrid}>
          {group.items.map((book: any) => renderGameCard(book, group))}
        </div>
      </section>
    ));

  const renderEmptyState = () => (
    <div className={classes.emptyState}>
      <h2 className={classes.emptyTitle}>
        {activeTab === TAB_EBOOK
          ? locale.emptyEbookTitle
          : activeTab === TAB_GAME
          ? locale.emptySeriousGameTitle
          : locale.emptyAllTitle}
      </h2>
      <p className={classes.emptyText}>{locale.emptyDescription}</p>
    </div>
  );

  const pageHeaderProps = {
    breadcrumbs: [{ label: locale.home, href: '/main' }, { label: locale.pageTitle }],
    title: locale.pageTitle,
    description: locale.pageDescription,
    titleVariant: 'primary' as const,
  };

  if (loading && !allBook.length && !list.length) {
    return (
      <RedesignLayout footer={true} noPadding={true}>
        <div className={sharedClasses.loadingCenter}>
          <CircularProgress />
        </div>
      </RedesignLayout>
    );
  }

  if (!allBook.length && !list.length) {
    return (
      <RedesignLayout footer={true} noPadding={true}>
        <div className={sharedClasses.pageShell}>
          <PageHeader {...pageHeaderProps} />
          {loading ? (
            <div className={sharedClasses.loadingCenter}>
          <CircularProgress />
        </div>
          ) : (
            renderEmptyState()
          )}
        </div>
      </RedesignLayout>
    );
  }

  return (
    <RedesignLayout footer={true} noPadding={true}>
      <div className={sharedClasses.pageShell}>
        <PageHeader {...pageHeaderProps} />
        <ResourceTabs activeTab={activeTab} onChange={setActiveTab} />

        {hasVisibleResources ? (
          <div className={classes.contentLayout}>
            <ResourceSidebar
              sections={summarySections}
              activeSectionId={activeSectionId}
              onSectionClick={scrollToSection}
            />
            <main className={classes.mainContent}>
              {showBooks && visibleBookGroups.length > 0 && (
                <div className={classes.resourceBlock}>
                  <div className={classes.blockHeader}>
                    <div className={classes.blockIconWrap}>
                      <BookOpenText className={classes.blockIcon} />
                    </div>
                    <h2 className={classes.blockTitle}>{locale.ebooksTitle}</h2>
                  </div>
                  {renderBookSections()}
                </div>
              )}
              {showGames && visibleGameGroups.length > 0 && (
                <div className={classes.resourceBlock}>
                  <div className={classes.blockHeader}>
                    <div className={classes.blockIconWrap}>
                      <SportsEsports className={classes.blockIcon} />
                    </div>
                    <h2 className={classes.blockTitle}>{locale.seriousGamesTitle}</h2>
                  </div>
                  {renderGameSections()}
                </div>
              )}
            </main>
          </div>
        ) : (
          renderEmptyState()
        )}
      </div>
    </RedesignLayout>
  );
};

export default AttachedResources;
